import React, { forwardRef, useCallback, useEffect, useId, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Gantt, Tooltip, Willow } from '@svar-ui/react-gantt';
import { Locale } from '@svar-ui/react-core';
import { getID } from '@svar-ui/lib-dom';
import { ru } from '@svar-ui/core-locales';
import { en as ganttEn } from '@svar-ui/gantt-locales';
import '@svar-ui/react-gantt/all.css';

// import RecordPreview from '../../common/UI/RecordPreview';
import SCENE_CONTAINER from '../../../configs/sceneContainer';
import sceneActions from '../../../actions/sceneActions';
import {
  SCALES,
  ZOOM_LEVELS,
  highlightWeekends,
  scaleToZoomLevel,
  unitsPerStep,
  zoomedScale,
} from '../helpers/scales';
import { centerOnDate, pxToDate } from '../helpers/geometry';
import { buildTaskColors } from '../helpers/colors';
import { recordsToTasks } from '../helpers/tasks';
import { recordsToLinks } from '../helpers/links';
import useChartLayout from '../hooks/useChartLayout.hook';
import useChartPan from '../hooks/useChartPan.hook';
import useColumns from '../hooks/useColumns.hook';
import TaskLabel from './TaskLabel';
import styles from '../gantt.less';

// свободная зона прокрутки по краям ленты +-полгода
const RANGE_AREA = 180 * 24 * 60 * 60 * 1000;

const LOCALE = { ...ganttEn, ...ru };

// Рамку выбранного бара рисуем правилом по data-id, а не классом через тип задачи:
// тип — часть данных, и его изменение заставило бы пересобрать все задачи разом
const pickedCss = (recordId, scope) =>
  recordId == null
    ? ''
    : `${scope} .wx-bar[data-id=${JSON.stringify(String(recordId))}]` +
      '{--bp-gantt-picked-outline:1px solid var(--wx-color-primary);}';

const GanttBody = forwardRef((props, ref) => {
  const {
    records,
    startId,
    endId,
    titleId,
    employeeId,
    progressId,
    scale,
    onScaleChange,
    onEditTask,
    sceneId,
    catalogId,
    // fields,
    gridWidth,
    displayMode,
    columnWidths,
    onGridWidthChange,
    onDisplayModeChange,
    onColumnWidthsChange,
    colorField,
    linkField,
    linkDirection,
    onEditLink,
    startField,
    endField,
    selectedRowsIds,
    onRowSelectionChange,
  } = props;

  const config = SCALES[scale];
  const chartId = useId();
  const chartScope = `[data-gantt-id=${JSON.stringify(chartId)}]`;

  const apiRef = useRef(null); // SVAR api ref
  const [ganttApi, setGanttApi] = useState(null);
  const resolveTooltip = (element) => {
    const employeeName = element.getAttribute('data-employee-name');
    if (employeeName) return employeeName;

    const taskId = getID(element, 'data-task-id');
    return taskId != null && ganttApi ? ganttApi.getTask(taskId)?.text : null;
  };
  const selectedTaskRef = useRef(null);
  const anchorDateRef = useRef(null); // дата под левым краем — держим её при смене масштаба

  const [selectedId, setSelectedId] = useState(null);
  // const selectedRecord = (selectedId != null && records && records.find((r) => r.get('id') === selectedId)) || null;

  const checked = useMemo(() => new Set(selectedRowsIds || []), [selectedRowsIds]);

  const toggleRecord = useCallback(
    (recordId) => {
      if (!onRowSelectionChange) return;

      const next = new Set(checked);
      if (next.has(recordId)) next.delete(recordId);
      else next.add(recordId);

      onRowSelectionChange([...next]);
    },
    [checked, onRowSelectionChange],
  );

  const columns = useColumns({ columnWidths, checked, onToggleRecord: toggleRecord, sceneId, catalogId });

  // Библиотека зовёт наш init ровно один раз: дальше при смене конфига она дёргает
  // store.init, а колбэк — нет. Поэтому обработчики ниже живут с замыканиями первого
  // рендера, и пропы им нужно читать через реф, иначе editTask будет вечно сверяться
  // с первым набором записей, а changeScale — с первым масштабом и адресом
  const latest = useRef(null);
  latest.current = {
    catalogId,
    sceneId,
    scale,
    config,
    canResize: Boolean(endId && endId !== startId),
    onEditTask,
    onEditLink,
    onScaleChange,
    onGridWidthChange,
    onDisplayModeChange,
    onColumnWidthsChange,
  };

  const zoomConfig = useMemo(() => ({ level: scaleToZoomLevel(scale), levels: ZOOM_LEVELS }), [scale]);

  const cell = config.scales[config.scales.length - 1];

  const highlightTime = useCallback(
    (date, unit) => [highlightWeekends(date, unit), unit === cell.unit && cell.css(date)].filter(Boolean).join(' '),
    [cell],
  );

  const { taskTypes, css: colorCss } = useMemo(() => buildTaskColors(colorField, chartScope), [colorField, chartScope]);

  const barsCss = colorCss + pickedCss(selectedId, chartScope);

  const tasks = useMemo(
    () =>
      recordsToTasks(records, {
        startId,
        endId,
        titleId,
        employeeId,
        progressId,
        cell,
        colorField,
        startField,
        endField,
      }),
    [records, startId, endId, titleId, employeeId, progressId, cell, colorField, startField, endField],
  );

  const linkFieldId = linkField && linkField.get('id');
  const links = useMemo(
    () => recordsToLinks(records, tasks, catalogId, linkFieldId, linkDirection),
    [records, tasks, catalogId, linkFieldId, linkDirection],
  );

  // TODO: Подумать о бесконечной ленте (будет нужно для перетаскивания задач на будущие периоды, например, в том числе и для suggestionRecords)
  const [minMs, maxMs] = useMemo(() => {
    const today = new Date().setHours(0, 0, 0, 0); // начало суток числом, как и границы ниже
    let min = today;
    let max = today;

    tasks.forEach((task) => {
      const s = task.start.getTime();
      const e = task.end.getTime();
      if (s < min) min = s;
      if (e > max) max = e;
    });

    return [min - RANGE_AREA, max + RANGE_AREA];
  }, [tasks]);

  const rangeStart = useMemo(() => new Date(minMs), [minMs]);
  const rangeEnd = useMemo(() => new Date(maxMs), [maxMs]);

  // Подводим диаграмму к Сегодня или к последней записи
  const scrollTarget = useMemo(() => {
    let last = null;
    let lastIndex = -1;

    tasks.forEach((task, index) => {
      if (!last || task.end > last.end) {
        last = task;
        lastIndex = index;
      }
    });

    return { date: last ? last.end : new Date(), index: lastIndex };
  }, [tasks]);

  const selected = useMemo(() => (selectedId != null ? [selectedId] : undefined), [selectedId]);

  const boardRef = useChartLayout({ apiRef, scrollTarget });
  const { onPointerDown, onPointerMove, onPointerUp } = useChartPan({ apiRef, boardRef });

  // Отдаём родителю хендлер для кнопки Сегодня
  useImperativeHandle(ref, () => ({
    scrollTo: (date) => apiRef.current && centerOnDate(apiRef.current, date),
  }));

  const centerTask = useCallback((id) => {
    const api = apiRef.current;
    if (!api) return;

    const task = api.getTask(id);
    if (!task || !task.start) return;

    centerOnDate(api, task.start);
  }, []);

  // При смене масштаба возвращаемся к выделенной записи, а если её нет, к дате, что была под левым краем
  useEffect(() => {
    const api = apiRef.current;
    if (!api) return;

    const id = selectedTaskRef.current;
    if (id) {
      centerTask(id);
      return;
    }

    if (anchorDateRef.current) api.exec('scroll-chart', { date: anchorDateRef.current });
  }, [scale, centerTask]);

  // Режим грида приходится досылать вручную: правило стора выводит displayMode из
  // _compactMode и вне компактного режима всегда возвращает 'all', затирая сохранённый
  useEffect(() => {
    if (!displayMode) return undefined;

    const frame = requestAnimationFrame(() => {
      const api = apiRef.current;
      if (api && api.getState().displayMode !== displayMode) {
        api.exec('set-display-mode', { mode: displayMode });
      }
    });

    return () => cancelAnimationFrame(frame);
  }, [displayMode]);

  const initApi = useCallback(
    (api) => {
      apiRef.current = api;
      setGanttApi(api);

      // До первого измерения SVAR присылает отрицательные размеры, из-за которых
      // виртуализация заголовка показывает почти всю шкалу вместо видимой области.
      api.intercept('resize-chart', ({ width, height }) => width > 0 && height > 0);

      api.on('select-task', (ev) => {
        const { id, focus } = ev;
        selectedTaskRef.current = id;

        if (focus) {
          // клик по строке грида сам панель не открывает, только подводит ленту.
          // Но если запись уже открыта, переключаем её на выбранную строку
          centerTask(id);
          setSelectedId((prev) => (prev == null ? null : id));
        }
      });

      // Двойной клик по строке грида и по бару шлёт show-editor. Свой редактор задач нам
      // не нужен, вместо него открываем карточку записи — так же, как иконка в колонке Id
      api.intercept('show-editor', ({ id }) => {
        if (id != null) {
          const { catalogId: cid, sceneId: sid } = latest.current;
          sceneActions.openRecord({ catalogId: cid, recordId: id, parentSceneId: sid }, {}, SCENE_CONTAINER.POPUP);
        }
        return false;
      });

      api.intercept('move-task', () => false);
      api.intercept('drag-task', (ev) => {
        if (ev.top !== undefined) return false;
        return latest.current.canResize || ev.width === undefined || ev.width === api.getTask(ev.id).$w;
      });

      api.intercept('update-task', (ev) => {
        const { id, task, diff, inProgress } = ev;
        if (inProgress || !task) return;

        const perStep = unitsPerStep(latest.current.config);
        const steps = diff ? Math.round(diff / perStep) || Math.sign(diff) : 0;
        // eslint-disable-next-line no-param-reassign
        if (diff) ev.diff = steps * perStep;

        const accepted = latest.current.onEditTask({
          id,
          steps,
          movedStart: 'start' in task,
          movedEnd: 'end' in task,
          progress: 'progress' in task ? task.progress : undefined,
        });
        if (accepted === false) return false;

        // делаем выбранной изменяемую запись
        selectedTaskRef.current = id;
        setSelectedId(id);
        return undefined;
      });

      api.intercept('add-link', ({ link }) => {
        latest.current.onEditLink({ ...link, type: 'e2s' });
        return false;
      });
      api.intercept('delete-link', ({ id }) => {
        const link = api.getState().links.byId(id);
        if (link) latest.current.onEditLink(link, true);
        return false;
      });
      api.intercept('update-link', () => false);

      // Зум колесом считаем сами. Свой обработчик библиотеки на этом событии заодно
      // пересчитывает единицу длины — и ищет её в списке, где минут нет, получая undefined;
      // а ещё переводит позицию курсора в дату, прибавляя часы независимо от единицы.
      // Плавности мы при этом не теряем: ширина ячейки у нас задана пресетом намертво,
      // и любой поворот колеса всё равно сразу переключает масштаб
      api.intercept('zoom-scale', ({ dir }) => {
        const next = zoomedScale(latest.current.scale, dir);
        if (next) latest.current.onScaleChange(next);
        return false;
      });

      api.on('scroll-chart', () => {
        const { scrollLeft } = api.getState();
        const date = pxToDate(api, scrollLeft || 0);
        if (date) anchorDateRef.current = date;
      });

      api.on('resize-grid', ({ width }) => latest.current.onGridWidthChange(width));
      api.on('set-display-mode', ({ mode }) => latest.current.onDisplayModeChange(mode));
      api.on('set-columns', ({ columns: next }) => {
        if (!next) return;

        const widths = {};
        next.forEach((column) => {
          if (column.id && column.width) widths[column.id] = column.width;
        });

        latest.current.onColumnWidthsChange(widths);
      });
    },
    [centerTask],
  );

  return (
    <div className={styles.body} data-gantt-id={chartId}>
      {barsCss ? <style>{barsCss}</style> : null}

      <div
        className={styles.board}
        data-links-enabled={Boolean(linkField)}
        ref={boardRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
      >
        <Willow>
          <Locale words={LOCALE}>
            <Tooltip api={ganttApi} resolver={resolveTooltip} css={styles.taskTooltip}>
              <Gantt
                zoom={zoomConfig}
                init={initApi}
                tasks={tasks}
                links={links}
                taskTypes={taskTypes}
                taskTemplate={TaskLabel}
                columns={columns}
                selected={selected}
                gridWidth={gridWidth}
                displayMode={displayMode}
                cellHeight={Number(styles.rowHeight)}
                scaleHeight={Number(styles.scaleHeight)}
                lengthUnit={config.lengthUnit || 'day'}
                durationUnit="minute"
                highlightTime={highlightTime}
                start={rangeStart}
                end={rangeEnd}
              />
            </Tooltip>
          </Locale>
        </Willow>
      </div>

      {/* RecordPreview
      {selectedRecord && (
        <div className={styles.preview}>
          <RecordPreview
            className={styles.previewPanel}
            sceneId={sceneId}
            catalogId={catalogId}
            recordId={selectedId}
            record={selectedRecord}
            fields={fields}
            onClose={() => setSelectedId(null)}
          />
        </div>
      )}
      */}
    </div>
  );
});

export default GanttBody;
